import { useEffect, useState } from "react";
import { api, type ListingSettingsDTO } from "../../lib/api";
import { Spinner } from "../../components/States";
import { fmtMoney } from "../../lib/format";

// Listing mode and prices, editable without a deploy.
//
// Amounts are held as strings while typing. Parsing on every keystroke turns
// "4." into 4 and fights the person entering 4.50, and a price field that
// rewrites what you typed is the fastest way to charge the wrong number.

const CURRENCIES = ["usd", "eur", "gbp", "inr", "aud", "cad", "sgd"];

const toField = (cents: number | null) => (cents == null ? "" : (cents / 100).toFixed(2));

/** "" means not priced. Returns undefined for anything that is not a number. */
function toCents(field: string): number | null | undefined {
  const t = field.trim();
  if (t === "") return null;
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return undefined;
  return Math.round(parseFloat(t) * 100);
}

export function Pricing() {
  const [settings, setSettings] = useState<ListingSettingsDTO | null>(null);
  const [mode, setMode] = useState<ListingSettingsDTO["mode"]>("free");
  const [currency, setCurrency] = useState("usd");
  const [token, setToken] = useState("");
  const [company, setCompany] = useState("");
  const [managed, setManaged] = useState("");
  const [sla, setSla] = useState("24");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const load = (s: ListingSettingsDTO) => {
    setSettings(s);
    setMode(s.mode);
    setCurrency(s.currency);
    setToken(toField(s.tokenListingCents));
    setCompany(toField(s.companyListingCents));
    setManaged(toField(s.managedCents));
    setSla(String(s.slaHours));
  };

  useEffect(() => {
    api
      .adminListingSettings()
      .then(load)
      .catch((e) => setError((e as Error).message));
  }, []);

  const save = async () => {
    setError(null);
    setFlash(null);

    const cents = { token: toCents(token), company: toCents(company), managed: toCents(managed) };
    for (const [label, v] of Object.entries(cents)) {
      if (v === undefined) return setError(`The ${label} price is not an amount.`);
    }
    const slaHours = Number(sla);
    if (!Number.isInteger(slaHours) || slaHours < 1 || slaHours > 720) {
      return setError("The decision window must be a whole number of hours, 1 to 720.");
    }

    setBusy(true);
    try {
      const saved = await api.adminSaveListingSettings({
        mode,
        currency,
        tokenListingCents: cents.token ?? null,
        companyListingCents: cents.company ?? null,
        managedCents: cents.managed ?? null,
        slaHours,
      });
      load(saved);
      setFlash("Saved. This applies to the next submission.");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!settings && !error) return <Spinner />;

  const preview = (field: string) => {
    const c = toCents(field);
    return c == null || c === undefined ? null : fmtMoney(c, currency);
  };

  return (
    <div className="narrow-lg">
      <h2>Listing mode &amp; pricing</h2>
      <p className="muted">
        What it costs to submit a listing, and whether a free lane exists at all. Saved settings
        take effect on the next request- no deploy.
      </p>

      {error && <p className="notice notice-error">{error}</p>}
      {flash && <p className="notice notice-ok">{flash}</p>}

      <section className="card pricing-card section">
        <h3>Who can submit a listing</h3>
        <div className="grid gap-8 mt-8">
          {(
            [
              ["free", "Free", "Anyone signed in can submit at no cost."],
              ["paid", "Paid only", "A submission requires payment before it enters the queue."],
              ["both", "Both", "The submitter chooses: free lane, or pay to be decided sooner."],
            ] as const
          ).map(([value, label, help]) => (
            <label key={value} className="radio-row">
              <input
                type="radio"
                name="listing-mode"
                checked={mode === value}
                onChange={() => setMode(value)}
              />
              <span>
                <strong>{label}</strong>
                <span className="muted radio-help">{help}</span>
              </span>
            </label>
          ))}
        </div>
      </section>

      <section className="card pricing-card section">
        <h3>Prices</h3>
        <p className="muted">
          Leave a field empty to stop selling that item. An empty price with paid-only mode is
          refused when you save, because it would leave submitters told to pay with no way to.
        </p>

        <label className="field">
          <span>Currency</span>
          <select className="input" value={currency} onChange={(e) => setCurrency(e.target.value)}>
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>{c.toUpperCase()}</option>
            ))}
          </select>
        </label>

        {(
          [
            ["Token listing", token, setToken, "Charged when someone submits a token."],
            ["Company listing", company, setCompany, "Charged for an exchange, wallet or protocol."],
            ["Managed onboarding", managed, setManaged, "Staff build the profile. Sold on its own."],
          ] as const
        ).map(([label, value, set, help]) => (
          <label className="field" key={label}>
            <span>
              {label}
              <span className="muted field-help">{help}</span>
            </span>
            <span className="price-input">
              <input
                className="input"
                inputMode="decimal"
                placeholder="not sold"
                value={value}
                onChange={(e) => set(e.target.value)}
              />
              <span className="muted price-preview">{preview(value) ?? "—"}</span>
            </span>
          </label>
        ))}
      </section>

      <section className="card pricing-card section">
        <h3>Decision window</h3>
        <p className="muted">
          What a paid submission buys: a decision within this many hours. It is not an approval —
          a paid submission that gets rejected is refunded automatically.
        </p>
        <label className="field">
          <span>Hours</span>
          <input
            className="input"
            inputMode="numeric"
            value={sla}
            onChange={(e) => setSla(e.target.value)}
          />
        </label>
      </section>

      <div className="row gap-8 section">
        <button className="btn btn-primary" onClick={save} disabled={busy}>
          {busy ? "Saving…" : "Save settings"}
        </button>
        {settings?.updatedAt && (
          <span className="muted">Last changed {settings.updatedAt.slice(0, 10)}</span>
        )}
      </div>

      <p className="muted section">
        Stripe&rsquo;s secret key stays in the deployment environment; it is never editable here.
        Prices set on this page are sent to Stripe with each checkout, so a change needs no work
        in the Stripe dashboard.
      </p>
    </div>
  );
}
